// D69 — Pfad-/Glob-Primitive der Projekt-Daten-Ebene. SICHERHEITSKRITISCH: `normalizeProjectPath`
// ist das Sandbox-Primitiv beider Adapter (FS-Access + CLI-Client) — alle Pfade werden hart auf den
// Projekt-Root eingegrenzt (kein `..`-Ausbruch, kein absoluter Pfad). Rein (keine I/O) ⇒ unit-testbar.

/**
 * Pfad des generierten Manifests, das der Web-Editor lädt (D70). EINE geteilte Konstante für Loader
 * (`useProject`), Live-Sync (`useProjectSync`) UND den Collision-Manifest-Mirror (D85) — sonst könnten die
 * drei divergieren (Codex-P1). KNOWN-GAP: der CLI-`generate` schreibt nach `config.paths.manifest`
 * (in `loumy.project.toml` überschreibbar); der Web-Editor unterstützt aktuell NUR diesen Default —
 * ein custom Pfad ließe den Editor builtins-only laden (kein Projekt-Placeable ⇒ Collision-UI gar nicht
 * erreichbar). Das volle Auslesen aus der `project.toml` ist ein separater Folge-Schritt (Editor_PRD §13).
 */
export const EDITOR_MANIFEST_FILE = "loumy.manifest.json";

/**
 * Unterscheidet „Datei fehlt" (erwartet, weich) von einem echten Lese-/Permission-/I/O-Fehler (hart).
 * FS-Access wirft eine `DOMException` mit Name `NotFoundError`; der CLI-Client wirft einen `Error` mit
 * „HTTP 404"/„not found" (siehe `cliSource.req`). Genutzt, um eine fehlende OPTIONALE Datei sauber als
 * „leer/Default" zu behandeln, OHNE echte Fehler zu verschlucken (sonst Überschreib-/Drift-Gefahr).
 * Rein (keine I/O) — der einzige geteilte Ort dieser Konvention (kein Duplikat je Konsument).
 *
 * AUSSCHLIESSLICH auf den Reject von `ProjectSource.readFile(...)` anwenden — NICHT auf Decode-/JSON-/
 * Schema-Fehler: deren Texte könnten „404"/„not found" enthalten und so fälschlich als „fehlt" gelten
 * (Codex-P1). In diesem engen Kontext bedeutet die Text-/`NotFoundError`-Prüfung verlässlich „Datei fehlt".
 */
export function isFileMissing(e: unknown): boolean {
  if (typeof DOMException !== "undefined" && e instanceof DOMException && e.name === "NotFoundError") return true; // FS-Access
  const msg = e instanceof Error ? e.message : String(e);
  return /\b404\b/.test(msg) || /not found/i.test(msg); // CLI: HTTP 404
}

/** Geworfen, wenn ein Pfad den Projekt-Root verlassen würde oder syntaktisch unzulässig ist. */
export class UnsafePathError extends Error {
  constructor(
    readonly path: string,
    reason: string,
  ) {
    super(`Unsicherer Projekt-Pfad '${path}': ${reason}`);
    this.name = "UnsafePathError";
  }
}

// NUL + C0-Steuerzeichen (Code Points 0–31): in Pfaden nie legitim. Per charCodeAt geprüft (kein
// Control-Char/Escape im Quelltext) — beugt Truncation-/Smuggling-Tricks gegen einen evtl. doppelt
// dekodierenden CLI-Server vor.
function hasControlChar(s: string): boolean {
  for (let i = 0; i < s.length; i++) {
    if (s.charCodeAt(i) <= 0x1f) return true;
  }
  return false;
}

/**
 * Normalisiert einen Projekt-relativen Pfad und grenzt ihn HART auf den Root ein. Wirft
 * `UnsafePathError` bei Steuerzeichen, absolutem Pfad, Laufwerks-Präfix, `..`-Segment oder leerem
 * Ergebnis. Rückgabe: kanonischer POSIX-Pfad ohne führenden Slash, ohne `.`/leere Segmente.
 * Hinweis: Percent-Sequenzen werden NICHT dekodiert ⇒ `%2e%2e` bleibt ein literaler Dateiname (kein
 * Traversal); ein nachgelagerter CLI-Server darf Pfade folglich nicht erneut dekodieren.
 */
export function normalizeProjectPath(path: string): string {
  if (typeof path !== "string" || path.length === 0) throw new UnsafePathError(String(path), "leer");
  if (hasControlChar(path)) throw new UnsafePathError(path, "Steuerzeichen");
  // Backslashes auf POSIX vereinheitlichen (Windows-Eingaben).
  const unified = path.replace(/\\/g, "/");
  // Absolute Pfade + Laufwerks-Präfixe (C:) verboten — der Root ist das Projekt, nicht das Dateisystem.
  if (unified.startsWith("/")) throw new UnsafePathError(path, "absoluter Pfad");
  if (/^[a-zA-Z]:/.test(unified)) throw new UnsafePathError(path, "Laufwerks-Präfix");
  const out: string[] = [];
  for (const seg of unified.split("/")) {
    if (seg === "" || seg === ".") continue;
    if (seg === "..") throw new UnsafePathError(path, "'..'-Segment (Root-Ausbruch)");
    out.push(seg);
  }
  if (out.length === 0) throw new UnsafePathError(path, "zeigt auf den Root selbst");
  return out.join("/");
}

/** Zerlegt einen (bereits normalisierten) Pfad in Segmente. */
export function pathSegments(path: string): string[] {
  return normalizeProjectPath(path).split("/");
}

/** Verzeichnisteil eines Pfads ("" für Top-Level-Dateien). */
export function dirOf(path: string): string {
  const segs = pathSegments(path);
  return segs.slice(0, -1).join("/");
}

/** Letztes Segment (Dateiname) eines Pfads. */
export function baseName(path: string): string {
  const segs = pathSegments(path);
  return segs[segs.length - 1]!;
}

/** Fügt Segmente zu einem normalisierten Pfad zusammen (sandboxed). */
export function joinPath(...parts: string[]): string {
  return normalizeProjectPath(parts.filter((p) => p.length > 0).join("/"));
}

const GLOB_SPECIAL = /[.+^${}()|[\]\\]/g;
/** Obergrenze der Glob-Länge — schützt vor quadratischem Matching großer Wildcard-Ketten (ReDoS-Schranke). */
const MAX_GLOB_LENGTH = 256;

/**
 * Übersetzt einen Glob in eine anchored RegExp. Unterstützt `*` (beliebig außer `/`), `**`
 * (beliebig inkl. `/`) und `?` (genau ein Zeichen außer `/`). Alle übrigen Regex-Sonderzeichen
 * werden escaped — kein Injektion über den Glob; die Längen-Schranke begrenzt die Match-Kosten.
 */
export function globToRegExp(glob: string): RegExp {
  if (glob.length > MAX_GLOB_LENGTH) throw new Error(`Glob zu lang (>${MAX_GLOB_LENGTH} Zeichen).`);
  let re = "";
  for (let i = 0; i < glob.length; i++) {
    const c = glob[i]!;
    if (c === "*") {
      if (glob[i + 1] === "*") {
        re += ".*"; // ** — über Verzeichnisgrenzen
        i++;
      } else {
        re += "[^/]*"; // * — innerhalb eines Segments
      }
    } else if (c === "?") {
      re += "[^/]";
    } else {
      re += c.replace(GLOB_SPECIAL, "\\$&");
    }
  }
  return new RegExp(`^${re}$`);
}

/** Prüft, ob ein normalisierter Pfad auf einen Glob passt. */
export function matchGlob(glob: string, path: string): boolean {
  return globToRegExp(glob).test(path);
}

/**
 * Literaler Verzeichnis-Präfix eines Globs (alles vor dem ersten Wildcard-Segment) — erlaubt es,
 * nur den relevanten Teilbaum zu durchlaufen statt des ganzen Projekts. `"levels/*.json"` → `"levels"`.
 */
export function globPrefix(glob: string): string {
  const segs = glob.split("/");
  const literal: string[] = [];
  for (const seg of segs) {
    if (seg.includes("*") || seg.includes("?")) break;
    literal.push(seg);
  }
  // Das letzte literale Segment kann eine Datei sein; nur Verzeichnis-Präfix zurückgeben.
  return literal.slice(0, literal.length === segs.length ? literal.length - 1 : literal.length).join("/");
}
